import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { KstDataGrid } from '@/components/KstDataGrid';
import type {
  KstDataGridColumn,
  KstDataGridPager,
  KstDataGridRows,
} from '@/components/KstDataGrid';

import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import moment from 'moment';
import { hasValue, getOfficerLabel } from '@/utils/legacy-utils';

// ============================================================================
// External Declarations
// ============================================================================

import { getOfficerPluralLabel, getCurrentUserId } from '@/utils/auth';

// Lazy wrappers: these window globals are set by MyBuiltScripts.js which loads
// after ReactBundle.js. Wrapping them defers resolution to call time.
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const getGeolocation = _wg('getGeolocation');

// ============================================================================
// Constants
// ============================================================================

const PAGE_SIZE = 50;

// ============================================================================
// URL Helpers
// ============================================================================

function getEmsHref(lat: number, lng: number): string {
  return `https://www.google.com/maps/search/emergency+medical+services/@${lat},${lng},14z/`;
}

function getFireHref(lat: number, lng: number): string {
  return `https://www.google.com/maps/search/fire-department/@${lat},${lng},14z/`;
}

function getPoliceHref(lat: number, lng: number): string {
  return `https://www.google.com/maps/search/police+department/@${lat},${lng},14z/`;
}

function getHospitalHref(lat: number, lng: number): string {
  return `https://www.google.com/maps/search/hospital+emergency+room/@${lat},${lng},14z/`;
}

// ============================================================================
// Grid Columns
// ============================================================================

function getGridColumns(): KstDataGridColumn[] {
  return [
    {
      field: 'officer',
      name: getOfficerLabel(),
      size: 'col-xs-4',
    },
    {
      field: 'ems',
      name: 'EMS',
      size: 'col-xs-2',
      template: (e: any) => {
        if (hasValue(e) && hasValue(e.latitude) && hasValue(e.longitude)) {
          const href = getEmsHref(e.latitude, e.longitude);
          return `<a target="_blank" href="${href}" class="kstc-btn kstc-btn--ems kstc-btn--sm">EMS</a>`;
        }
        return '';
      },
    },
    {
      field: 'fire',
      name: 'Fire Dept',
      size: 'col-xs-2',
      template: (e: any) => {
        if (hasValue(e) && hasValue(e.latitude) && hasValue(e.longitude)) {
          const href = getFireHref(e.latitude, e.longitude);
          return `<a target="_blank" href="${href}" class="kstc-btn kstc-btn--fire kstc-btn--sm">Fire</a>`;
        }
        return '';
      },
    },
    {
      field: 'police',
      name: 'Police Dept',
      size: 'col-xs-2',
      template: (e: any) => {
        if (hasValue(e) && hasValue(e.latitude) && hasValue(e.longitude)) {
          const href = getPoliceHref(e.latitude, e.longitude);
          return `<a target="_blank" href="${href}" class="kstc-btn kstc-btn--police kstc-btn--sm">Police</a>`;
        }
        return '';
      },
    },
    {
      field: 'hospital',
      name: 'Hospital',
      size: 'col-xs-2',
      template: (e: any) => {
        if (hasValue(e) && hasValue(e.latitude) && hasValue(e.longitude)) {
          const href = getHospitalHref(e.latitude, e.longitude);
          return `<a target="_blank" href="${href}" class="kstc-btn kstc-btn--hospital kstc-btn--sm">Hospital</a>`;
        }
        return '';
      },
    },
  ];
}

// ============================================================================
// EmergencyLookupScreen Component
// ============================================================================

export function EmergencyLookupScreen() {
  const { theme } = useTheme();

  // --- Labels ---
  const officerPluralLabel = useMemo(() => getOfficerPluralLabel(), []);
  const isMobileModule = useMemo(() => App.getModuleTypeFromURL() === 'mdt', []);
  const officersHeader = useMemo(
    () => isMobileModule ? `Other ${officerPluralLabel}` : officerPluralLabel,
    [isMobileModule, officerPluralLabel]
  );

  // --- Location State ---
  const [currentLatitude, setCurrentLatitude] = useState<number | null>(null);
  const [currentLongitude, setCurrentLongitude] = useState<number | null>(null);

  // --- Grid State ---
  const [gridRows, setGridRows] = useState<KstDataGridRows>({ entries: null });
  const [isOfficersLoading, setIsOfficersLoading] = useState(false);
  const [gridPager, setGridPager] = useState<KstDataGridPager>({
    curPage: 1,
    numPages: 1,
    records: 0,
    isVisible: false,
  });

  // --- Refs ---
  const currentPageRef = useRef(1);
  const isFirstLoadRef = useRef(true);
  const refreshCallbackRef = useRef<(() => void) | null>(null);

  // --- Column Definitions ---
  const gridColumns = useMemo(() => getGridColumns(), []);

  // --- Computed ---
  const hasLocation = useMemo(
    () => hasValue(currentLatitude) && hasValue(currentLongitude),
    [currentLatitude, currentLongitude]
  );

  const emsHref = useMemo(
    () => hasLocation ? getEmsHref(currentLatitude!, currentLongitude!) : '#',
    [hasLocation, currentLatitude, currentLongitude]
  );

  const fireHref = useMemo(
    () => hasLocation ? getFireHref(currentLatitude!, currentLongitude!) : '#',
    [hasLocation, currentLatitude, currentLongitude]
  );

  const policeHref = useMemo(
    () => hasLocation ? getPoliceHref(currentLatitude!, currentLongitude!) : '#',
    [hasLocation, currentLatitude, currentLongitude]
  );

  const hospitalHref = useMemo(
    () => hasLocation ? getHospitalHref(currentLatitude!, currentLongitude!) : '#',
    [hasLocation, currentLatitude, currentLongitude]
  );

  // --- Update Location ---
  const updateLocation = useCallback(() => {
    getGeolocation(
      (successResult) => {
        setCurrentLatitude(successResult.coords['latitude']);
        setCurrentLongitude(successResult.coords['longitude']);
      },
      () => {
        // Location unavailable - buttons will remain hidden
      }
    );
  }, []);

  // --- Load Officers ---
  const loadOfficers = useCallback(() => {
    setIsOfficersLoading(true);
    const currentUserId: number = getCurrentUserId();
    const authTokenRefresh = isFirstLoadRef.current;

    api.getMapLists(authTokenRefresh, true, (result: any) => {
      const personList = result.personList || [];
      const rows: any[] = [];

      for (const person of personList) {
        if (person.id !== currentUserId) {
          rows.push({
            id: person.id,
            officer: person.name,
            latitude: person.latitude,
            longitude: person.longitude,
          });
        }
      }

      const totalRecords = rows.length;
      const currentPage = currentPageRef.current;

      setGridRows({ entries: rows });
      setGridPager({
        curPage: currentPage,
        numPages: Math.ceil(totalRecords / PAGE_SIZE),
        records: totalRecords,
        isVisible: totalRecords > PAGE_SIZE,
      });

      isFirstLoadRef.current = false;
      setIsOfficersLoading(false);
    }, () => {
      App.showPopupNotification('Error getting lookup info.', 'error');
      setIsOfficersLoading(false);
    });
  }, []);

  // --- Pagination ---
  const goToPage = useCallback((page: number) => {
    currentPageRef.current = page;
    loadOfficers();
  }, [loadOfficers]);

  // --- Refresh ---
  const refreshGrid = useCallback(() => {
    loadOfficers();
  }, [loadOfficers]);

  // Register refresh callback + auto-refresh
  useEffect(() => {
    refreshCallbackRef.current = () => {
      loadOfficers();
      updateLocation();
    };

    return () => {
      refreshCallbackRef.current = null;
    };
  }, [loadOfficers, updateLocation]);

  // --- Initial Load ---
  useEffect(() => {
    updateLocation();
    loadOfficers();

    // Start auto-refresh every 60 seconds
    const interval = setInterval(() => {
      if (refreshCallbackRef.current) {
        refreshCallbackRef.current();
      }
    }, 60000);

    return () => {
      clearInterval(interval);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // --- SVG Icons ---
  const emsSvg = (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M22 12H18L15 21L9 3L6 12H2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const fireSvg = (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8.5 14.5A2.5 2.5 0 0011 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 11-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 002.5 2.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const policeSvg = (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const hospitalSvg = (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 7v4" />
      <path d="M14 21v-3a2 2 0 0 0-4 0v3" />
      <path d="M14 9h-4" />
      <path d="M18 11h2a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2h2" />
      <path d="M18 21V5a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16" />
    </svg>
  );

  // --- Render ---
  return (
    <div className={`emergency-lookup-screen ${theme}`.trim()}>
      <div className="kst-page-container">
        <div className="kst-layout kst-layout--no-sidebar">
          <div className="kst-main-content">
            <div className="kst-page">
              <div className="kst-page-header">
                <h3 className="kst-page-title">Emergency Lookup</h3>
                <p className="kst-page-description">
                  Find nearby emergency services for yourself or other {officerPluralLabel.toLowerCase()}.
                </p>
              </div>

              {/* Primary Action: My Location Buttons */}
              <div className="kst-emergency-primary">
                {hasLocation ? (
                  <>
                    <a href={emsHref} target="_blank" className="kst-emergency-btn kst-emergency-btn--ems" title="Find EMS near my location" rel="noopener noreferrer">
                      <div className="kst-emergency-btn__icon">{emsSvg}</div>
                      <div className="kst-emergency-btn__label">EMS</div>
                      <div className="kst-emergency-btn__sublabel">Near Me</div>
                    </a>
                    <a href={fireHref} target="_blank" className="kst-emergency-btn kst-emergency-btn--fire" title="Find Fire Department near my location" rel="noopener noreferrer">
                      <div className="kst-emergency-btn__icon">{fireSvg}</div>
                      <div className="kst-emergency-btn__label">Fire Dept</div>
                      <div className="kst-emergency-btn__sublabel">Near Me</div>
                    </a>
                    <a href={policeHref} target="_blank" className="kst-emergency-btn kst-emergency-btn--police" title="Find Police Department near my location" rel="noopener noreferrer">
                      <div className="kst-emergency-btn__icon">{policeSvg}</div>
                      <div className="kst-emergency-btn__label">Police Dept</div>
                      <div className="kst-emergency-btn__sublabel">Near Me</div>
                    </a>
                    <a href={hospitalHref} target="_blank" className="kst-emergency-btn kst-emergency-btn--hospital" title="Find Hospital near my location" rel="noopener noreferrer">
                      <div className="kst-emergency-btn__icon">{hospitalSvg}</div>
                      <div className="kst-emergency-btn__label">Hospital</div>
                      <div className="kst-emergency-btn__sublabel">Near Me</div>
                    </a>
                  </>
                ) : (
                  <div className="kst-emergency-loading-state">
                    <div className="kst-emergency-loading-spinner"></div>
                    <span>Fetching your location...</span>
                  </div>
                )}
              </div>

              {/* Section Divider */}
              <div className="kst-emergency-divider">
                <span>{officersHeader}</span>
              </div>

              {/* Officers Grid */}
              <KstDataGrid
                columns={gridColumns}
                rows={gridRows}
                pager={gridPager}
                showheader={false}
                showcheckbox={false}
                mobiletitlefield="officer"
                loading={isOfficersLoading}
                onGoToPage={goToPage}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmergencyLookupScreen;
